// 方舟一号 API 响应转换函数
import type {
  PortfolioData,
  PortfolioApiResponse,
  PerformanceData,
  PerformanceApiResponse,
  Position,
  PositionApiResponse,
  RebalancingRecord,
  RebalancingApiResponse,
  DividendInfo,
  PositionDistribution,
  ChartData
} from '@/types/arkOne'

/**
 * 转换组合数据
 */
export function transformPortfolioData(apiData: PortfolioApiResponse): PortfolioData {
  // 获取第一个策略数据
  const strategy = apiData.category_list[0]
  
  if (!strategy) {
    // 如果没有数据，返回空对象
    return {
      name: '',
      tags: [],
      highlights: '',
      annualizedReturn: 0,
      totalReturn: 0,
      totalDividendYield: 0,
      capitalDistribution: {
        stocks: 0,
        etf: 0,
        cash: 0,
        other: 0
      },
      positionDistribution: []
    }
  }

  // 计算持仓分布
  // 1. 按比例降序排序（不过滤 0 值）
  const sortedStocks = strategy.stock_list.slice().sort((a, b) => 
    parseFloat(b.stock_ratio) - parseFloat(a.stock_ratio)
  )
  
  // 2. 取前5名，其余合并为"其他"
  const top5 = sortedStocks.slice(0, 5)
  const others = sortedStocks.slice(5)
  
  const positionDistribution: PositionDistribution[] = top5.map(stock => ({
    name: stock.name,
    code: stock.symbol,
    percentage: parseFloat(stock.stock_ratio)
  }))
  
  // 3. 如果有超过5个持仓，将第6个及以后的合并为"其他"
  if (others.length > 0) {
    const othersPercentage = others.reduce((sum, stock) => 
      sum + parseFloat(stock.stock_ratio), 0
    )
    positionDistribution.push({
      name: '其他',
      code: 'others',
      percentage: othersPercentage
    })
  }

  return {
    name: strategy.name,
    tags: strategy.labels,
    highlights: strategy.description,
    annualizedReturn: parseFloat(strategy.annual_yield) * 100, // 转换为百分比
    totalReturn: strategy.total_rate * 100, // 转换为百分比
    totalDividendYield: strategy.dividend_rate * 100, // 转换为百分比
    capitalDistribution: {
      stocks: strategy.allocate.stock_ratio * 100, // 转换为百分比
      etf: strategy.allocate.etf_ratio * 100, // 转换为百分比
      cash: strategy.allocate.cash_ratio * 100, // 转换为百分比
      other: strategy.allocate.other_ratio * 100 // 转换为百分比
    },
    positionDistribution
  }
}

/**
 * 将 ChartData 对象（/fz/charts 返回的 chart）转换为 PerformanceData[]
 */
export function transformChartData(chart: ChartData): PerformanceData[] {
  if (!chart || !Array.isArray(chart.date) || !Array.isArray(chart.list)) return []

  const { date, list, orders } = chart

  // 找到本组合和指数的数据
  const portfolioData = list.find(item => item.name === '本组合') || list[0]
  const indexData = list.find(item => item.name === '指数' || item.name.includes('指数'))

  if (!portfolioData) return []

  // 按日期分组调仓记录
  const ordersByDate = new Map<string, typeof orders>()
  if (Array.isArray(orders)) {
    orders.forEach(order => {
      const dateKey = order.trade_date
      if (!ordersByDate.has(dateKey)) {
        ordersByDate.set(dateKey, [])
      }
      ordersByDate.get(dateKey)!.push(order)
    })
  }

  // 转换数据
  return date.map((dateStr, index) => {
    const result: PerformanceData = {
      date: dateStr,
      returnRate: (portfolioData.data[index] || 0) * 100, // 转换为百分比
      indexReturnRate: indexData ? (indexData.data[index] || 0) * 100 : 0 // 转换为百分比
    }

    // 检查当天是否有调仓
    const dayOrders = ordersByDate.get(dateStr)
    if (dayOrders && dayOrders.length > 0) {
      result.rebalancing = {
        adjustments: dayOrders.map(order => ({
          stockName: order.name,
          direction: order.bs_flag === 'B' ? '调入' : '调出'
        }))
      }
    }

    return result
  })
}

/**
 * 转换业绩数据（从组合数据中提取）
 * @deprecated 建议直接使用 chart 对象传递给 PerformanceChart 组件，无需转换
 * 该函数保留用于向后兼容，新代码应该直接使用 API 返回的 chart 对象
 */
export function transformPerformanceData(apiData: PortfolioApiResponse): PerformanceData[] {
  const strategy = apiData.category_list[0]
  
  if (!strategy || !strategy.chart) {
    return []
  }

  const { date, list, orders } = strategy.chart
  
  // 找到本组合和指数的数据
  const portfolioData = list.find(item => item.name === '本组合')
  const indexData = list.find(item => item.name === '指数' || item.name.includes('指数'))
  
  if (!portfolioData) {
    return []
  }

  // 按日期分组调仓记录
  const ordersByDate = new Map<string, typeof orders>()
  orders.forEach(order => {
    const dateKey = order.trade_date
    if (!ordersByDate.has(dateKey)) {
      ordersByDate.set(dateKey, [])
    }
    ordersByDate.get(dateKey)!.push(order)
  })

  // 转换数据
  return date.map((dateStr, index) => {
    const result: PerformanceData = {
      date: dateStr,
      returnRate: (portfolioData.data[index] || 0) * 100, // 转换为百分比
      indexReturnRate: indexData ? (indexData.data[index] || 0) * 100 : 0 // 转换为百分比
    }

    // 检查当天是否有调仓
    const dayOrders = ordersByDate.get(dateStr)
    if (dayOrders && dayOrders.length > 0) {
      result.rebalancing = {
        adjustments: dayOrders.map(order => ({
          stockName: order.name,
          direction: order.bs_flag === 'B' ? '调入' : '调出'
        }))
      }
    }

    return result
  })
}

/**
 * 转换持仓数据
 */
export function transformPositions(apiData: PositionApiResponse): Position[] {
  const strategy = apiData.category_list[0]
  
  if (!strategy || !strategy.stock_list) {
    return []
  }

  // 过滤掉 stock_ratio 为 0 的持仓
  const nonZeroPositions = strategy.stock_list.filter(item => {
    const ratio = parseFloat(item.stock_ratio)
    return ratio > 0
  })

  return nonZeroPositions.map((item, index) => {
    // 解析派息信息
    let latestDividend: DividendInfo | undefined
    let latestDividendContent: string | undefined
    
    if (item.latest_dividend?.content) {
      // 保存原始派息信息文本
      latestDividendContent = item.latest_dividend.content
      
      // 解析格式：除净日：2026-02-01；派息：2026-03-31 每股派息0.08港元。
      const content = item.latest_dividend.content
      const dateMatch = content.match(/派息：(\d{4}-\d{2}-\d{2})/)
      const amountMatch = content.match(/每股派息([\d.]+)/)
      const currencyMatch = content.match(/(港元|美元|人民币)/)
      
      if (dateMatch && amountMatch) {
        latestDividend = {
          date: dateMatch[1],
          sharesPerDividend: 1, // 默认每1股
          amountPerShare: parseFloat(amountMatch[1]),
          currency: currencyMatch ? (currencyMatch[1] === '港元' ? 'HKD' : currencyMatch[1] === '美元' ? 'USD' : 'CNY') : item.ccy
        }
      }
    }

    return {
      sequence: index + 1,
      name: item.name,
      code: `${item.market}.${item.symbol}`, // 组合为完整代码
      symbol: item.symbol,
      market: item.market,
      exchange: item.exchange, // 保留交易所字段
      simulatedReturn: item.rate * 100, // 转换为百分比
      cost: parseFloat(item.avg_cost),
      currentPrice: parseFloat(item.last_price),
      holdingPercentage: parseFloat(item.stock_ratio),
      openingDate: item.entry_date.split(' ')[0], // 只取日期部分
      annualizedDividendYield: item.dividend_yield * 100, // 转换为百分比
      latestDividend,
      latestDividendContent,
      isClosed: false // API 数据中没有这个字段，默认为 false
    }
  })
}

/**
 * 转换调仓历史数据
 */
export function transformRebalancingHistory(apiData: RebalancingApiResponse | { list: any[] } | null | undefined): RebalancingRecord[] {
  // 严格的空值检查
  if (!apiData) {
    console.warn('transformRebalancingHistory: apiData 为空')
    return []
  }
  
  if (!apiData.list) {
    console.warn('transformRebalancingHistory: apiData.list 为空')
    return []
  }
  
  if (!Array.isArray(apiData.list)) {
    console.warn('transformRebalancingHistory: apiData.list 不是数组', apiData.list)
    return []
  }
  
  if (apiData.list.length === 0) {
    console.log('transformRebalancingHistory: apiData.list 是空数组')
    return []
  }

  // 使用类型断言来解决 TypeScript 类型推断问题
  const list = apiData.list as Array<{
    trade_date: string
    name: string
    symbol: string
    market: string
    exchange: string
    exec_price: number | string
    price: number | string
    stock_ratio: string
    pre_stock_ratio: string
    bs_flag: string
    expected: string
  }>

  return list.map(item => {
    const preRatio = parseFloat(item.pre_stock_ratio || '0')
    const currentRatio = parseFloat(item.stock_ratio || '0')
    const positionChange = currentRatio - preRatio

    return {
      date: item.trade_date || '',
      name: item.name || '',
      code: `${item.market}.${item.symbol}`, // 组合为完整代码
      symbol: item.symbol || '',
      market: item.market || '',
      exchange: item.exchange || '', // 添加交易所字段
      averagePrice: parseFloat(String(item.exec_price || item.price || '0')),
      positionChange: positionChange,
      preStockRatio: preRatio,
      stockRatio: currentRatio,
      direction: item.bs_flag === 'B' ? '买入' : '卖出',
      reason: item.expected || ''
    }
  })
}
